import * as Yup from "yup";
import { TFunction } from "i18next";
import { ValidationError } from "yup";
import { AlternativeUnitGroupInput, ProductInput, ProductProxy, UnitProxy } from "@me-team/host/main/graphql/types";
import { Option } from "./types";
import { FormikHelpers } from "formik";
import { DeepPartial } from "@apollo/client/utilities";

export const unitGroupQuantityId: number = 2;
export const unitGroupPackagingId: number = 5;
export const unitPCId: number = 5;
export const unitPackId: number = 12;
export const ALT_UNIT_GROUP_NONE_ID = -1 as const;

export const transformToNullableNumber = (_value: unknown, originalValue: unknown) => {
    if (originalValue === "" || originalValue === undefined || originalValue === null) return null;
    if (typeof originalValue === "string") {
        const n = Number(originalValue.trim());
        return Number.isFinite(n) ? n : null;
    }
    if (typeof originalValue === "number") return originalValue;

    return null;
};

export const createProductFormValidationSchema = (t: TFunction, isArchiveCategory?: boolean) =>
    Yup.object().shape({
        name: Yup.string().trim().required(t("validation.required")),
        brand: Yup.string().trim().required(t("validation.required")),
        category: Yup.number()
            .required(t("validation.required"))
            .test("not-archived-category", t("Select a non-archive category"), function () {
                return !isArchiveCategory;
            }),
        criticalResidue: Yup.number().nullable().moreThan(0, t("Numeric value, must be greater than 0")),
        price: Yup.number().required(t("validation.required")),
        unit: Yup.number().required(t("validation.required")),
        alternativeUnitGroupInput: Yup.object().shape({
            alternativeUnitGroup: Yup.number()
                .test("required-or-none", t("validation.required"), (val) => {
                    if (val === null || val === undefined) return false;
                    return val === ALT_UNIT_GROUP_NONE_ID || (typeof val === "number" && val > 0);
                })
                .required(t("validation.required")),
            alternativeValue: Yup.number()
                .transform(transformToNullableNumber)
                .nullable()
                .when("alternativeUnitGroup", {
                    is: (groupId: number | null) => groupId === ALT_UNIT_GROUP_NONE_ID,
                    then: (schema) => schema.notRequired().nullable(),
                    otherwise: (schema) =>
                        schema.typeError(t("validation.required")).required(t("validation.required")),
                }),
            alternativeUnit: Yup.number()
                .transform(transformToNullableNumber)
                .nullable()
                .when("alternativeUnitGroup", {
                    is: (groupId: number | null) => groupId === ALT_UNIT_GROUP_NONE_ID,
                    then: (schema) => schema.notRequired().nullable(),
                    otherwise: (schema) =>
                        schema.typeError(t("validation.required")).required(t("validation.required")),
                }),
        }),
        productAttributes: Yup.array()
            .nullable()
            .of(
                Yup.object().shape({
                    name: Yup.string()
                        .trim()
                        .required(t("validation.required"))
                        .max(20, t("Name must be less than 20 characters")),
                    property: Yup.string().trim().required(t("validation.required")),
                })
            )
            .test(
                "unique-name",
                null,
                function (this: Yup.TestContext, productAttributes: Array<{ name: string }> | null) {
                    if (!productAttributes) return true;

                    const errors: ValidationError[] = [];
                    const nameIndicesMap: Record<string, number[]> = {};

                    productAttributes.forEach((attr, index) => {
                        const name = attr.name;
                        if (name in nameIndicesMap) {
                            nameIndicesMap[name].push(index);
                        } else {
                            nameIndicesMap[name] = [index];
                        }
                    });

                    Object.keys(nameIndicesMap).forEach((name) => {
                        const indices = nameIndicesMap[name];
                        if (indices.length > 1) {
                            indices.slice(1).forEach((index) => {
                                errors.push(
                                    this.createError({
                                        path: `${this.path}[${index}].name`,
                                        message: t("Name already exists"),
                                    })
                                );
                            });
                        }
                    });

                    if (errors.length > 0) {
                        throw new ValidationError(errors);
                    }
                    return true;
                }
            )
            .test(
                "unique-property",
                null,
                function (this: Yup.TestContext, productAttributes: Array<{ property: string }> | null) {
                    if (!productAttributes) return true;

                    const errors: ValidationError[] = [];
                    const propertyIndicesMap: Record<string, number[]> = {};

                    productAttributes.forEach((attr, index) => {
                        const property = attr.property;
                        if (property in propertyIndicesMap) {
                            propertyIndicesMap[property].push(index);
                        } else {
                            propertyIndicesMap[property] = [index];
                        }
                    });

                    Object.keys(propertyIndicesMap).forEach((property) => {
                        const indices = propertyIndicesMap[property];
                        if (indices.length > 1) {
                            indices.slice(1).forEach((index) => {
                                errors.push(
                                    this.createError({
                                        path: `${this.path}[${index}].property`,
                                        message: t("Property already exists"),
                                    })
                                );
                            });
                        }
                    });

                    if (errors.length > 0) {
                        throw new ValidationError(errors);
                    }
                    return true;
                }
            ),
    });

export const getTextColorAvailabilityClass = (status: string, isArchive: boolean) => {
    if (isArchive) {
        return "text-secondary";
    }

    switch (status) {
        case "In stock":
            return "text-green";
        case "Out of stock":
            return "text-grey";
        case "Not added":
            return "text-dark";
        default:
            return "text-secondary";
    }
};

export const getMainUnitOptions = (unitsData: UnitProxy[]) => {
    if (!unitsData) return [];
    return unitsData.map((unit) => ({
        value: unit.id,
        label: unit.name,
    }));
};

export const getUnitLabelByValue = (
    options: Option[],
    value: number,
    labelType: "cost" | "sellingPrice" | "quantity" | "costPrice" = "cost"
): string => {
    switch (labelType) {
        case "sellingPrice":
            if (value === 5) return "1 pc";
            if (value === 12) return "1 pack";
            break;

        case "quantity":
            if (value === 5) return "1 Pc =";
            if (value === 12) return "1 Pack =";
            break;
        case "costPrice":
            if (value === 5) return "pc";
            if (value === 12) return "pack";
        case "cost":
        default:
            return options.find((option) => option.value === value)?.label || "";
    }
    return options.find((option) => option.value === value)?.label || "";
};

export const getAutoAltUnitId = (mainUnitId: number) => (mainUnitId === unitPackId ? unitPackId : unitPCId);
export const getUnitNameById = (mainUnitId: number, unitList: UnitProxy[]) =>
    unitList?.find((unit) => unit.id === mainUnitId)?.name;
export const getMainUnitGroup = (mainUnitId: number) =>
    mainUnitId === unitPCId ? unitGroupQuantityId : unitGroupPackagingId;

export const clearAltFields = (setFieldValue: FormikHelpers<ProductInput>["setFieldValue"]): void => {
    setFieldValue("alternativeUnitGroupInput.alternativeUnitGroup", null, false);
    setFieldValue("alternativeUnitGroupInput.alternativeUnit", null, false);
    setFieldValue("alternativeUnitGroupInput.alternativeValue", null, false);
};

export const clearAltErrors = (setFieldError: FormikHelpers<ProductInput>["setFieldError"]): void => {
    setFieldError("alternativeUnitGroupInput.alternativeUnitGroup", undefined);
    setFieldError("alternativeUnitGroupInput.alternativeUnit", undefined);
    setFieldError("alternativeUnitGroupInput.alternativeValue", undefined);
};

export const buildInitialAltGroup = (
    product: DeepPartial<ProductProxy>,
    mainUnitId: number
): AlternativeUnitGroupInput => {
    const groupId = product?.alternativeUnitGroup?.id ?? null;

    if (groupId == null) {
        const autoId = mainUnitId != null ? getAutoAltUnitId(mainUnitId) : null;
        return {
            alternativeUnitGroup: ALT_UNIT_GROUP_NONE_ID,
            alternativeUnit: autoId,
            alternativeValue: 1,
        };
    }

    return {
        alternativeUnitGroup: groupId,
        alternativeUnit: product?.alternativeUnit?.id ?? null,
        alternativeValue: product?.alternativeValue ?? null,
    };
};
